/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, Fragment, useEffect, useRef, useCallback } from "react"
import { notification } from "@duxton-web/ui"
import { OrdersOutlined } from "@duxton-web/icons"
import { useHistory } from "react-router-dom"
import { ORDER_MANAGER_URL_PREFIX } from "constants/routes"
import { isInOrderListPage } from "order-manager/utils/order_helper"
import { OrderNotification } from "order-manager/types/models/notification"
import { useDispatch, useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { actionCreator } from "store/store"
import { OrderPageType } from "order-manager/constants/orderState"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { useLazyFetchOrderListQuery } from "queries/orderManagerApi"
import {
  TabCardViewKeys,
  TabTableViewKeys,
} from "order-manager/constants/tabview"
import { autoAcceptGroupMap } from "order-manager/constants/autoAcceptGroup"
import { getFoodMerchantUrl } from "constants/domain"
import { ViewMode } from "order-manager/constants/viewMode"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import {
  setupBeforeUnloadListener,
  setupNotificationConfig,
  monitorActiveTab,
  updateOpenedTabsRegularly,
  showNotificationToUser,
} from "order-manager/utils/notification_helper"
import { CURRENT_TAB_KEY } from "constants/sessions_storage"
import { getOrderManagerNotificationConfig } from "order-manager/utils/storage_config_helper"
import { TroyWebTracker } from "utils/tracking"
import {
  EventAction,
  EventCategory,
  ShowOn,
} from "order-manager/constants/tracking"
import { storageHelper } from "utils/storage"
import { fetchOrderListWithMigration } from "utils/rtk_migration_helper"

interface OrderNotificationComponentProps {}

export const OrderNotificationComponent: FC<OrderNotificationComponentProps> = () => {
  // the latter part is for tests to run successfully
  const notificationAvailable = "Notification" in window

  if (
    notificationAvailable &&
    typeof Notification?.requestPermission === "function" &&
    Notification.permission !== "granted"
  ) {
    Notification.requestPermission()
  }

  const dispatch = useDispatch()
  const {
    flags: { fetchOrderList: isRtkFetchOrderListEnabled },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()
  const [triggerFetchOrderList] = useLazyFetchOrderListQuery()

  const {
    orderManagerMerchant: { maxAPIStore },
    orderManager: {
      newOrderNotification,
      orderNotificationCount,
      orderManagerConfig,
    },
  } = useSelector((state: AppState) => state)

  const history = useHistory()
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const fetchOrderList = useCallback(
    (notifMerchantID: string, pageType: string) => {
      if (isRtkFlagLoading) {
        return
      }

      fetchOrderListWithMigration({
        dispatch,
        fetchOrderListRtk: triggerFetchOrderList,
        isRtkFetchOrderListEnabled,
        merchantID: notifMerchantID,
        pageType,
        autoAcceptGroup:
          autoAcceptGroupMap.get(maxAPIStore?.merchant.autoAcceptGroup || "") ||
          0,
      })
    },
    [
      dispatch,
      isRtkFetchOrderListEnabled,
      isRtkFlagLoading,
      maxAPIStore,
      triggerFetchOrderList,
    ],
  )

  const textMap = useTranslationsMapTyped({
    header: "ordermanager.notification.header",
    body: "ordermanager.notification.body",
  })

  const addCounterToFavicon = (count: Number) => {
    const favicon = document.getElementById("favicon") as HTMLLinkElement
    const faviconSize = 64

    // Remove notification badge from favicon if count = 0
    if (count === 0) {
      favicon?.setAttribute("href", "/images/grabfavicon.ico")
      return
    }

    const canvas = document.createElement("canvas")
    canvas.width = faviconSize
    canvas.height = faviconSize

    const context = canvas.getContext("2d")
    const img = document.createElement("img")
    img.src = favicon.href

    img.onload = () => {
      if (context !== null) {
        // Draw Original Favicon as Background
        context.drawImage(img, 0, 0, faviconSize, faviconSize)

        // Draw Notification Circle
        context.beginPath()
        context.arc(
          canvas.width - faviconSize / 3,
          faviconSize / 3,
          faviconSize / 3,
          0,
          2 * Math.PI,
        )
        context.fillStyle = "#FF0000"
        context.fill()

        // Draw Notification Number
        context.font = '35px "helvetica", sans-serif'
        context.textAlign = "center"
        context.textBaseline = "middle"
        context.fillStyle = "#FFFFFF"
        context.fillText(
          count.toString(),
          canvas.width - faviconSize / 3,
          faviconSize / 3,
        )
      }

      // Replace favicon
      favicon?.setAttribute("href", canvas.toDataURL("image/png"))
    }
  }

  const clickNotification = (
    newOrder: OrderNotification,
    fromHiddenState: boolean,
  ) => {
    const notifConfig = getOrderManagerNotificationConfig()
    if (notifConfig.activeTabID === sessionStorage.getItem(CURRENT_TAB_KEY)) {
      window.focus()
    }

    const selectedTab =
      orderManagerConfig?.defaultView === ViewMode.Card
        ? TabCardViewKeys.ongoing
        : TabTableViewKeys.preparing

    if (!isInOrderListPage() || fromHiddenState) {
      history.push(
        `${ORDER_MANAGER_URL_PREFIX}/${newOrder?.merchantID}/${selectedTab}/${newOrder?.orderID}`,
      )
      dispatch(actionCreator.orderManager.changeOrderNotificationCount(0))
    }
  }

  const displayNotification = (newOrder: OrderNotification) => {
    let showOn: string = ShowOn.Unknown

    if (document.visibilityState === "visible") {
      const key = newOrder?.orderID || `notif-${Date.now()}`
      notification.open({
        key,
        placement: "topRight",
        duration: 5,
        icon: <OrdersOutlined style={{ color: "#00b14f", fontSize: 24 }} />,
        message: textMap.header.replace(
          /<short.order.id>/i,
          newOrder?.shortOrderID || "",
        ),
        description: textMap.body,
        style: {
          cursor: isInOrderListPage() ? "text" : "pointer",
        },
        onClick: () => {
          clickNotification(newOrder, false)
          notification.destroy(key)
        },
      })

      showOn = ShowOn.InsidePageApp
    }

    if (document.visibilityState === "hidden" && notificationAvailable) {
      const title = textMap.header.replace(
        /<short.order.id>/i,
        newOrder?.shortOrderID || "",
      )
      const options = {
        body: textMap.body,
        icon: "/images/grabfavicon.ico",
        data: {
          orderID: newOrder?.orderID,
          merchantID: newOrder?.merchantID,
          shortOrderID: newOrder?.shortOrderID,
          tabID: sessionStorage.getItem(CURRENT_TAB_KEY),
        },
      }

      const notif = showNotificationToUser(title, options)
      if (notif) {
        notif.onclick = () => clickNotification(newOrder, true)
      }

      showOn = ShowOn.OutsidePageApp
    }

    TroyWebTracker.sendEvent({
      category: EventCategory.notification,
      action: EventAction.displayMedia,
      labels: {
        SHOW_ON: showOn,
        NOTIFICATION_SOURCE: newOrder.source,
        ...(newOrder.isRetryFromMonitoring && {
          SWITCHED_BACK_TO_HERMES: newOrder.isRetryFromMonitoring,
        }),
      },
    })
  }

  useEffect(() => {
    const cleanupBeforeUnload = setupBeforeUnloadListener()
    setupNotificationConfig()
    monitorActiveTab()
    updateOpenedTabsRegularly()

    // portalEnableHermesConnection was confirmed off for everyone in production
    // (BP-58506), so the fallback notification engine is always the active path.
    const notifConfig = getOrderManagerNotificationConfig()
    notifConfig.enableFallback = true
    notifConfig.enableHermes = false
    storageHelper.setOrderManagerNotificationConfig(notifConfig)

    return () => {
      cleanupBeforeUnload()
    }
  }, [])

  const clickNotificationRef = useRef(clickNotification)
  useEffect(() => {
    clickNotificationRef.current = clickNotification
  })
  const fetchOrderListRef = useRef(fetchOrderList)
  useEffect(() => {
    fetchOrderListRef.current = fetchOrderList
  })

  useEffect(() => {
    const onSwMessage = (event: MessageEvent) => {
      if (event.data?.type === "notificationclick") {
        // Use the order from the SW message so clicking an older notification
        // navigates to that specific order, not whatever Redux state holds now.
        const rawOrder = event.data?.order
        const tabID = rawOrder?.tabID
        const order = rawOrder as OrderNotification
        // Only handle if this tab was the one that showed the notification,
        // so all open tabs don't simultaneously navigate.
        const currentTabID = sessionStorage.getItem(CURRENT_TAB_KEY)
        if (!tabID || tabID === currentTabID) {
          clickNotificationRef.current(order, true)
        }
      }
    }

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", onSwMessage)
    }

    return () => {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("message", onSwMessage)
      }
    }
  }, [])

  useEffect(() => {
    const cleanup = () => {}

    if (!newOrderNotification?.merchantID) {
      return cleanup
    }

    if (notificationAvailable && Notification.permission === "granted") {
      displayNotification(newOrderNotification)

      // Lazy-load audio: create only when needed, then reuse
      if (!audioRef.current) {
        audioRef.current = new Audio(
          `${getFoodMerchantUrl()}/sounds/order-new.mp3`,
        )
      }
      audioRef.current.play().catch(error => {
        TroyWebTracker.sendEvent({
          category: EventCategory.notification,
          action: EventAction.errorPlayingSound,
          labels: {
            ERROR: error,
          },
        })
      })
    }

    setTimeout(() => {
      dispatch(actionCreator.orderManager.changeOrderNotificationCount(0))
    }, 10000)

    // Prevent reload if merchant is not in order page or skipRefreshOrderList is true
    if (!isInOrderListPage() || newOrderNotification?.skipRefreshOrderList) {
      return cleanup
    }

    const notifMerchantID = newOrderNotification?.merchantID || ""
    fetchOrderListRef.current(notifMerchantID, OrderPageType.PREPARINGV2)
    fetchOrderListRef.current(notifMerchantID, OrderPageType.READY)
    fetchOrderListRef.current(notifMerchantID, OrderPageType.UPCOMING)

    return cleanup
  }, [newOrderNotification])

  useEffect(() => {
    addCounterToFavicon(orderNotificationCount)
  }, [orderNotificationCount])

  return <Fragment />
}
